import React, { useState, useEffect } from 'react';
import { Bell, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { playNotificationSound } from '@/services/notificationService';

const NotificationPermissionPopup = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [hasResponded, setHasResponded] = useState(false);

  useEffect(() => {
    // Check if user has already responded or if permission is already granted/denied
    const hasSeenPrompt = localStorage.getItem('brownstone_notification_prompt_seen');
    
    if (hasSeenPrompt) {
      return;
    }

    // Check current permission status
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        // Show our custom popup after a delay
        const timer = setTimeout(() => {
          setShowPopup(true);
        }, 5000); // Show after 5 seconds
        
        return () => clearTimeout(timer);
      }
    }
  }, []);

  const handleAllow = async () => {
    setHasResponded(true);
    localStorage.setItem('brownstone_notification_prompt_seen', 'true');
    
    // Play a preview sound
    playNotificationSound();
    
    // Request actual browser permission
    if ('Notification' in window) {
      await Notification.requestPermission();
    }
    
    setShowPopup(false);
  };

  const handleDismiss = () => {
    setHasResponded(true);
    localStorage.setItem('brownstone_notification_prompt_seen', 'true');
    setShowPopup(false);
  };

  if (!showPopup || hasResponded) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] animate-in fade-in duration-300"
        onClick={handleDismiss}
      />
      
      {/* Popup */}
      <div 
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[101] w-[90%] max-w-md animate-in zoom-in-95 fade-in duration-300"
        data-testid="notification-permission-popup"
      >
        <div className="relative bg-gradient-to-br from-obsidian-light to-obsidian border border-electric/30 rounded-2xl overflow-hidden shadow-2xl shadow-electric/20">
          {/* Decorative elements */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-electric/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-electric/10 rounded-full blur-3xl" />
          
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="relative p-8 text-center">
            {/* Bell Icon with animation */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-electric/20 flex items-center justify-center">
                  <Bell className="w-10 h-10 text-electric animate-pulse" />
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center animate-bounce">
                  <Sparkles className="w-3 h-3 text-white" />
                </div>
              </div>
            </div>

            {/* Title */}
            <h3 className="font-display text-2xl text-white mb-3">
              Stay in the <span className="text-electric">Loop</span>
            </h3>
            
            {/* Description */}
            <p className="text-white/60 text-sm mb-6 max-w-xs mx-auto leading-relaxed">
              Get notified when new shows are announced, exclusive events drop, 
              and when there's buzz at BrownStone Jazz Club.
            </p>

            {/* Benefits */}
            <div className="flex justify-center gap-6 mb-6 text-left">
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 rounded-full bg-electric mt-1.5" />
                <span className="text-white/50 text-xs">New show alerts</span>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 rounded-full bg-electric mt-1.5" />
                <span className="text-white/50 text-xs">Exclusive updates</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-3">
              <Button 
                onClick={handleAllow}
                className="w-full bg-electric text-obsidian hover:bg-electric-light font-medium py-5 gap-2"
              >
                <Bell className="w-4 h-4" />
                Enable Notifications
              </Button>
              <button 
                onClick={handleDismiss}
                className="text-white/40 hover:text-white/60 text-sm transition-colors"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotificationPermissionPopup;
